/**
 * 遺伝子解析のチャンク保管（IndexedDB）。
 * 染色体級（chr1 ≈249 Mbp）を丸ごとメモリに置かず、可視範囲だけ読み出すための土台（§0.1 / §0.2）。
 *
 * ストア構成
 * - `chunks`   : キー `${projectId}/${sequenceId}/${index}` → 塩基コード（1 byte/塩基）
 * - `sequences`: キー `${projectId}/${sequenceId}` → 配列メタ（長さ・位相・チャンク数）
 * - `features` : キー `${projectId}/${sequenceId}` → 注釈配列
 */

import type { ChunkSource, GenomicFeatureV1, Topology } from '@kensuke/genome-core';

const DB_NAME = 'kensuke-gene-analysis';
const DB_VERSION = 1;
export const CHUNK_STORE = 'chunks';
export const SEQUENCE_STORE = 'sequences';
export const FEATURE_STORE = 'features';

export interface StoredSequenceMeta {
  key: string;
  projectId: string;
  sequenceId: string;
  name: string;
  description: string;
  length: number;
  topology: Topology;
  chunkSize: number;
  chunkCount: number;
  featureCount: number;
  updatedAt: number;
}

function chunkKey(projectId: string, sequenceId: string, index: number): string {
  return `${projectId}/${sequenceId}/${index}`;
}

function sequenceKey(projectId: string, sequenceId: string): string {
  return `${projectId}/${sequenceId}`;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function openGeneAnalysisDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(CHUNK_STORE)) db.createObjectStore(CHUNK_STORE);
      if (!db.objectStoreNames.contains(SEQUENCE_STORE)) db.createObjectStore(SEQUENCE_STORE);
      if (!db.objectStoreNames.contains(FEATURE_STORE)) db.createObjectStore(FEATURE_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

/** 取込中に呼ぶ。1 トランザクションで複数チャンクを書く。 */
export async function putChunks(
  projectId: string,
  sequenceId: string,
  chunks: Array<{ index: number; codes: Uint8Array }>
): Promise<void> {
  if (chunks.length === 0) return;
  const db = await openGeneAnalysisDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(CHUNK_STORE, 'readwrite');
    const store = tx.objectStore(CHUNK_STORE);
    for (const chunk of chunks) {
      store.put(chunk.codes, chunkKey(projectId, sequenceId, chunk.index));
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function getChunk(
  projectId: string,
  sequenceId: string,
  index: number
): Promise<Uint8Array | undefined> {
  const db = await openGeneAnalysisDb();
  const tx = db.transaction(CHUNK_STORE, 'readonly');
  const value = await requestToPromise<unknown>(
    tx.objectStore(CHUNK_STORE).get(chunkKey(projectId, sequenceId, index))
  );
  if (!value) return undefined;
  return value instanceof Uint8Array ? value : new Uint8Array(value as ArrayBuffer);
}

export async function putSequenceMeta(meta: Omit<StoredSequenceMeta, 'key' | 'updatedAt'>): Promise<void> {
  const db = await openGeneAnalysisDb();
  const record: StoredSequenceMeta = {
    ...meta,
    key: sequenceKey(meta.projectId, meta.sequenceId),
    updatedAt: Date.now(),
  };
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(SEQUENCE_STORE, 'readwrite');
    tx.objectStore(SEQUENCE_STORE).put(record, record.key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function listSequenceMeta(projectId: string): Promise<StoredSequenceMeta[]> {
  const db = await openGeneAnalysisDb();
  const tx = db.transaction(SEQUENCE_STORE, 'readonly');
  const all = await requestToPromise<StoredSequenceMeta[]>(
    tx.objectStore(SEQUENCE_STORE).getAll() as IDBRequest<StoredSequenceMeta[]>
  );
  return all
    .filter((meta) => meta.projectId === projectId)
    .sort((a, b) => a.sequenceId.localeCompare(b.sequenceId));
}

export async function putFeatures(
  projectId: string,
  sequenceId: string,
  features: GenomicFeatureV1[]
): Promise<void> {
  const db = await openGeneAnalysisDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(FEATURE_STORE, 'readwrite');
    tx.objectStore(FEATURE_STORE).put(features, sequenceKey(projectId, sequenceId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getFeatures(
  projectId: string,
  sequenceId: string
): Promise<GenomicFeatureV1[]> {
  const db = await openGeneAnalysisDb();
  const tx = db.transaction(FEATURE_STORE, 'readonly');
  const value = await requestToPromise<GenomicFeatureV1[] | undefined>(
    tx.objectStore(FEATURE_STORE).get(sequenceKey(projectId, sequenceId)) as IDBRequest<
      GenomicFeatureV1[] | undefined
    >
  );
  return value ?? [];
}

/** 取込をやり直すときに前回分を消す */
export async function clearProject(projectId: string): Promise<void> {
  const db = await openGeneAnalysisDb();
  const prefix = `${projectId}/`;
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([CHUNK_STORE, SEQUENCE_STORE, FEATURE_STORE], 'readwrite');
    for (const storeName of [CHUNK_STORE, SEQUENCE_STORE, FEATURE_STORE]) {
      const store = tx.objectStore(storeName);
      const range = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
      store.delete(range);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** 取込失敗時に本体を消さないための staging プロジェクト ID */
export function importStagingProjectId(projectId: string): string {
  return `${projectId}__importing`;
}

/**
 * staging に書いた取込結果を本番 projectId へ移す。
 * 成功確定後にだけ本体を wipe するので、失敗しても前回データが残る。
 */
export async function promoteImportStaging(projectId: string, stagingProjectId: string): Promise<void> {
  if (projectId === stagingProjectId) return;
  const db = await openGeneAnalysisDb();
  const stagingPrefix = `${stagingProjectId}/`;
  const livePrefix = `${projectId}/`;

  await clearProject(projectId);

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([CHUNK_STORE, SEQUENCE_STORE, FEATURE_STORE], 'readwrite');

    const copyStore = (storeName: string, rewriteMeta: boolean) => {
      const store = tx.objectStore(storeName);
      const range = IDBKeyRange.bound(stagingPrefix, `${stagingPrefix}\uffff`);
      const request = store.openCursor(range);
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        const oldKey = String(cursor.key);
        const newKey = livePrefix + oldKey.slice(stagingPrefix.length);
        let value = cursor.value;
        if (rewriteMeta && value && typeof value === 'object' && 'projectId' in value) {
          value = { ...value, projectId, key: newKey };
        }
        store.put(value, newKey);
        cursor.continue();
      };
    };

    copyStore(CHUNK_STORE, false);
    copyStore(SEQUENCE_STORE, true);
    copyStore(FEATURE_STORE, false);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });

  await clearProject(stagingProjectId);
}

/**
 * `ChunkedSequence` へ渡す遅延読み込み源。可視範囲のチャンクだけ IndexedDB から取る。
 */
export class IndexedDbChunkSource implements ChunkSource {
  constructor(
    private readonly projectId: string,
    private readonly sequenceId: string,
    readonly chunkSize: number,
    readonly chunkCount: number
  ) {}

  async readChunk(index: number): Promise<Uint8Array> {
    const chunk = await getChunk(this.projectId, this.sequenceId, index);
    if (!chunk) throw new Error(`チャンクが見つかりません: ${this.sequenceId}#${index}`);
    return chunk;
  }
}
